import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, KeyRound, Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ChangePassword() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const forced = !!user?.must_change_password;
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const same = next.length > 0 && next === current;

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next || !confirm) {
      toast.error("Fill in every field");
      return;
    }
    if (tooShort) { toast.error("New password must be at least 8 characters"); return; }
    if (mismatch) { toast.error("New password and confirmation do not match"); return; }
    if (same) { toast.error("New password must be different from current"); return; }
    setSaving(true);
    try {
      await api.post("/me/change-password", { current_password: current, new_password: next });
      // Refresh /me so the must_change_password flag clears and routing unblocks.
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch (_e) { /* ignore */ }
      toast.success("Password updated — keep it safe");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update password");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="change-password-page">
      {!forced && (
        <Link to="/dashboard" data-testid="cp-back" className="inline-flex items-center gap-2 text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </Link>
      )}

      {forced && (
        <div className="border-l-4 border-[#FF4500] bg-[#1a0a05] p-4 flex items-start gap-3" data-testid="cp-forced-banner">
          <AlertTriangle className="w-5 h-5 text-[#FF4500] mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-[#FF4500] mb-1">Set a new password to continue</div>
            <p className="text-sm text-[#E5E5E5] leading-relaxed">
              An admin reset your password. For security, please choose a new one of your own before you can use the rest of the app.
            </p>
          </div>
        </div>
      )}

      <div className="fade-up">
        <div className="label-mono mb-2" style={{ color: "#CCFF00" }}>
          <KeyRound className="w-3.5 h-3.5 inline mr-1" /> ACCOUNT SECURITY
        </div>
        <h1 className="h-display text-4xl sm:text-5xl tracking-tighter mb-2">{forced ? "Set new password." : "Change password."}</h1>
        <p className="text-sm text-[#A3A3A3] mb-8 max-w-xl">
          {forced
            ? "Enter the temporary password your admin shared with you, then pick a new password only you know."
            : "Set a new password for your Rally Lab account. You'll need your current password to confirm it's you."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-4 max-w-lg fade-up d1">
        <Field
          label={forced ? "Temporary password (from admin)" : "Current password"} testid="cp-current"
          type={show ? "text" : "password"} value={current} onChange={setCurrent}
          autoComplete="current-password"
        />
        <Field
          label="New password" testid="cp-new"
          type={show ? "text" : "password"} value={next} onChange={setNext}
          autoComplete="new-password"
          hint={tooShort ? "At least 8 characters" : same ? "Must differ from current password" : "8+ characters recommended"}
          hintColor={tooShort || same ? "#FF4500" : "#A3A3A3"}
        />
        <Field
          label="Confirm new password" testid="cp-confirm"
          type={show ? "text" : "password"} value={confirm} onChange={setConfirm}
          autoComplete="new-password"
          hint={mismatch ? "Doesn't match new password" : ""}
          hintColor="#FF4500"
        />

        <button type="button" onClick={() => setShow((s) => !s)} data-testid="cp-toggle-visibility"
          className="text-xs text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5">
          {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {show ? "Hide passwords" : "Show passwords"}
        </button>

        <div className="border border-[#262626] p-3 flex items-start gap-2 text-xs text-[#A3A3A3]">
          <ShieldCheck className="w-4 h-4 text-[#CCFF00] mt-0.5 shrink-0" />
          Stored hashed (bcrypt). After saving, any active password-reset links you may have received will be invalidated.
        </div>

        <button type="submit" disabled={saving || tooShort || mismatch || same || !current || !next || !confirm}
          data-testid="cp-submit"
          className="btn-volt w-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40">
          {saving ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type, autoComplete, testid, hint, hintColor }) {
  return (
    <div>
      <div className="label-mono mb-2">{label} <span className="text-[#FF4500]">*</span></div>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete} data-testid={testid} required
        className="w-full bg-[#141414] border border-[#262626] px-3 py-2.5 text-sm focus:outline-none focus:border-[#CCFF00]"
      />
      {hint && <div className="text-[10px] mt-1.5 uppercase tracking-wider" style={{ color: hintColor || "#A3A3A3" }}>{hint}</div>}
    </div>
  );
}
